package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToOneOrNull
import com.tedd.db.TeddDatabase
import com.tedd.db.User_profile
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.nowMillis

/**
 * Profil utilisateur synchronisé entre devices (table singleton user_profile,
 * id = "me"). Contient les champs identitaires (fullname, email, avatar, bio)
 * + les préférences portables (langue, thème, finance, pomodoro, notifs).
 *
 * Les vraies préférences "device-only" (creds Supabase, biométrie, backup
 * auto/freq, hideBalance) restent dans [PreferencesRepository] — elles n'ont
 * pas de sens cross-device.
 */
class UserProfileRepository(private val db: TeddDatabase) {

    fun observe(): Flow<User_profile?> =
        db.userProfileQueries.selectById(SINGLETON_ID)
            .asFlow().mapToOneOrNull(Dispatchers.Default)

    suspend fun get(): User_profile? = withContext(Dispatchers.Default) {
        db.userProfileQueries.selectById(SINGLETON_ID).executeAsOneOrNull()
    }

    /**
     * Garantit qu'une ligne existe (avec les valeurs par défaut). Idempotent —
     * à appeler une fois au démarrage de l'app, avant d'instancier AppSettings.
     */
    suspend fun ensureExists() = withContext(Dispatchers.Default) {
        val existing = db.userProfileQueries.selectByIdAnyState(SINGLETON_ID).executeAsOneOrNull()
        if (existing == null) {
            val now = nowMillis()
            db.userProfileQueries.insert(
                SINGLETON_ID,
                /* fullname */ "",
                /* email */ "",
                /* avatar_url */ "",
                /* bio */ "",
                /* language */ "fr",
                /* theme_mode */ "System",
                /* finance_default_period */ "all",
                /* finance_default_currency */ "XOF",
                /* pomodoro_focus_min */ 25L,
                /* pomodoro_short_break_min */ 5L,
                /* pomodoro_long_break_min */ 15L,
                /* notify_habits */ 1L,
                /* notify_deadlines */ 1L,
                /* notify_pomodoro_end */ 1L,
                /* notify_budget_alerts */ 1L,
                /* created_at */ now,
                /* updated_at */ now,
            )
        }
    }

    /** Met à jour tous les champs du profil + bump updated_at pour trigger la sync. */
    suspend fun update(
        fullname: String,
        email: String,
        avatarUrl: String,
        bio: String,
        language: String,
        themeMode: String,
        financeDefaultPeriod: String,
        financeDefaultCurrency: String,
        pomodoroFocusMin: Long,
        pomodoroShortBreakMin: Long,
        pomodoroLongBreakMin: Long,
        notifyHabits: Long,
        notifyDeadlines: Long,
        notifyPomodoroEnd: Long,
        notifyBudgetAlerts: Long,
    ) = withContext(Dispatchers.Default) {
        db.userProfileQueries.update(
            fullname, email, avatarUrl, bio, language, themeMode,
            financeDefaultPeriod, financeDefaultCurrency,
            pomodoroFocusMin, pomodoroShortBreakMin, pomodoroLongBreakMin,
            notifyHabits, notifyDeadlines, notifyPomodoroEnd, notifyBudgetAlerts,
            nowMillis(),
            SINGLETON_ID,
        )
    }

    companion object {
        /** Singleton id : une seule ligne par utilisateur, on n'a pas (encore) de multi-tenant. */
        const val SINGLETON_ID = "me"
    }
}
